
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth-provider';
import { toast } from 'sonner';

export interface LegalDocument {
  id: string;
  display_name: string;
  file_url: string;
  file_size: number;
  upload_date: string;
  is_active: boolean;
  created_by: string;
}

export const useLegalDocuments = () => {
  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .order('upload_date', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error: any) {
      console.error('Error fetching legal documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const uploadDocument = async (file: File, displayName: string) => {
    if (!user) {
      toast.error('You must be logged in to upload documents');
      return null;
    }

    setUploading(true);
    try {
      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = fileName;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('legal-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('legal-documents')
        .getPublicUrl(filePath);

      // Save document metadata
      const { data: docData, error: docError } = await supabase
        .from('legal_documents')
        .insert({
          display_name: displayName,
          file_url: urlData.publicUrl,
          file_size: file.size,
          created_by: user.id
        })
        .select()
        .single();

      if (docError) throw docError;

      toast.success('Document uploaded successfully');
      await fetchDocuments();
      return docData;
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const toggleDocumentActive = async (documentId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('legal_documents')
        .update({ is_active: isActive })
        .eq('id', documentId);

      if (error) throw error;

      toast.success(`Document ${isActive ? 'activated' : 'deactivated'}`);
      await fetchDocuments();
    } catch (error: any) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  };

  const deleteDocument = async (documentId: string, fileUrl: string) => {
    try {
      // Extract file path from URL
      const url = new URL(fileUrl);
      const filePath = url.pathname.split('/').pop();

      // Delete from storage
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('legal-documents')
          .remove([filePath]);

        if (storageError) {
          console.warn('Storage deletion error:', storageError);
        }
      }

      // Delete from database
      const { error } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      toast.success('Document deleted successfully');
      await fetchDocuments();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const getActiveDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('is_active', true)
        .order('display_name');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching active documents:', error);
      return [];
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  return {
    documents,
    loading,
    uploading,
    uploadDocument,
    toggleDocumentActive,
    deleteDocument,
    getActiveDocuments,
    refetch: fetchDocuments
  };
};
